/**
 * Sample-robustness ("threshold tier") helpers.
 *
 * The Worldpanel backend tags each metric value with a `threshold_tier`,
 * derived from the raw household sample size, so the frontend can caveat
 * low-sample data without recalculating thresholds:
 *
 *   2                raw HH sample N >= 60        -> render normally
 *   1                raw HH sample 30 <= N < 60   -> low sample (gray text)
 *   0                raw HH sample N < 30         -> suppressed (render "-")
 *   null             no data for the cell         -> suppressed (render "-")
 *   undefined/absent column carries no tier       -> no treatment
 *
 * Two payload shapes:
 *   - Flat: a single `threshold_tier` column governs the row.
 *   - Pivoted (durations): value columns fan out as `{metric}__{i}` and each
 *     pairs with `threshold_tier__{i}` by index.
 *
 * The thresholds (30 / 60) live on the backend (60 is currently hardcoded;
 * both become user-permissioned later); the frontend only reads the tier.
 */

import { COLOR_MIDDLEWEIGHT } from '../colors';

export const THRESHOLD_TIER_KEY = 'threshold_tier';

export const THRESHOLD_TIER = {
  LOW_SAMPLE: 1,
  NORMAL: 2,
  SUPPRESSED: 0,
};

/**
 * Returns the `threshold_tier` column key paired with a value-column accessor.
 * `buyers__0` -> `threshold_tier__0`; `buyers` -> `threshold_tier`.
 * @param  {string} accessorKey
 * @return {string}
 */
export const getThresholdTierKey = (accessorKey) => {
  if (typeof accessorKey !== 'string') return THRESHOLD_TIER_KEY;
  const durationIndex = accessorKey.match(/__(\d+)$/);
  return durationIndex ? `${THRESHOLD_TIER_KEY}__${durationIndex[1]}` : THRESHOLD_TIER_KEY;
};

/**
 * Reads the tier paired with a value-column accessor for a given row.
 * Returns `undefined` when the row carries no paired tier (i.e. no treatment).
 * @param  {Object} row         dataset row object (e.g. `cell.row.original`)
 * @param  {string} accessorKey value-column accessor (may be `;`-joined)
 * @return {number|null|undefined}
 */
export const getThresholdTier = (row, accessorKey) => {
  if (!row || typeof accessorKey !== 'string') return undefined;
  // Callers pass a single token in the hot path; only split when needed.
  const firstAccessor = accessorKey.includes(';') ? accessorKey.split(';')[0] : accessorKey;
  return row[getThresholdTierKey(firstAccessor)];
};

/**
 * Suppressed: below the very-low threshold (tier 0) or no data (null). Both
 * render as "-". `undefined` is NOT suppressed — it means the column carries
 * no tier and should be left untouched.
 * @param  {number|null|undefined} tier
 * @return {boolean}
 */
export const isSuppressedTier = (tier) => tier === THRESHOLD_TIER.SUPPRESSED || tier === null;

/**
 * Low sample: the value is shown but caveated with gray text.
 * @param  {number|null|undefined} tier
 * @return {boolean}
 */
export const isLowSampleTier = (tier) => tier === THRESHOLD_TIER.LOW_SAMPLE;

/**
 * Value (metric) columns carry a numeric format and therefore a sample tier;
 * dimension/label columns (text/url or no format) do not and must be left
 * untouched by tier treatment. Shared by the cell renderer and the copy path.
 * @param  {string} [format]
 * @return {boolean}
 */
export const isValueColumnFormat = (format) =>
  Boolean(format) && format !== 'text' && format !== 'url';

/**
 * Dark-gray text color for low-sample (and suppressed "-") cells. Sourced from
 * the design palette so it tracks the token rather than forking a literal; fall
 * back to COLOR_WELTERWEIGHT ('#669297') if this isn't distinct enough over the
 * cell hover highlight (per design).
 */
export const NON_ROBUST_TEXT_COLOR = COLOR_MIDDLEWEIGHT;
